import { IFormProfile } from "@multiweb/types/contracts";
import { AwaitableStandardEvent, Event } from "../../../../../Global/Base/Events";
import { IDisposable } from "../../../../../Global/Base/IDisposable";
import { FormProfileItemBuilder } from "../../DataBuilders/FormProfileItemBuilder";
import { DropDownMenuFormProfileItem } from "../Filters/DropDownMenuItems/DropDownMenuFormProfileItem";
import { FormProfileBasicItem } from "./FormProfileBasicItem";
import { IFormProfileDataContainer } from "./IFormProfileInfo";
/**
* Položka profilu. Zapouzdřuje základní informace o profilu formuláře a položky ve form menu a side panelu profilů.
* Umožňuje označení profilu (resp. jako defaultní) s notifikací pomocí eventů.
*/
export class FormProfileItem implements IFormProfile, IDisposable {
    public selectPreview: AwaitableStandardEvent<FormProfileItem, boolean> = new AwaitableStandardEvent<FormProfileItem, boolean>();
    public selectionChanged: Event<FormProfileItem, boolean> = new Event<FormProfileItem, boolean>();
    public defaultChanged: Event<FormProfileItem, boolean> = new Event<FormProfileItem, boolean>();

    private _isEdited: boolean = false;

    private _profileBasicItem: FormProfileBasicItem;
    private _sidePanelItem: FormProfileItemBuilder;
    private _formDropDownMenuItem: DropDownMenuFormProfileItem;
    private _formCode: string;
    private _isDisposed = false;

    public get id(): number {
        return this._profileBasicItem.id;
    }

    public get title(): string {
        return this._profileBasicItem.name;
    }

    public set title(value: string) {
        this._profileBasicItem.name = value;
        this._sidePanelItem.title = value;
        this._formDropDownMenuItem.profileName = value;
    }

    public get locKey(): string {
        return this._profileBasicItem.locKey;
    }

    public get formCode(): string {
        return this._formCode;
    }

    public get selected(): boolean {
        return this._profileBasicItem.selected;
    }

    public set selected(value: boolean) {
        const oldValue = this._profileBasicItem.selected;

        this.selectPreview.trigger(this, value).then((cancelEvent) => {
            if (value !== oldValue) {
                if (!cancelEvent) { // pokud došlo ke zrušení eventu, nepokračovat
                    this._profileBasicItem.selected = value;
                    if (this._sidePanelItem)
                        this._sidePanelItem.selected = value;
                    if (this._formDropDownMenuItem)
                        this._formDropDownMenuItem.selected = value;
                    if (value) {
                        this.selectionChanged.trigger(this, value);
                        if (this._sidePanelItem && value)
                            this._sidePanelItem.scrollIntoView();
                    }
                }
            }
        });
    }

    public get default(): boolean {
        return this._profileBasicItem.defaultProfile;
    }

    public set default(value: boolean) {
        const oldValue = this._profileBasicItem.defaultProfile;
        this._profileBasicItem.defaultProfile = value;
        if (this._sidePanelItem)
            this._sidePanelItem.isDefault = value;
        if (this._formDropDownMenuItem)
            this._formDropDownMenuItem.defaultProfile = value;
        if (value && value !== oldValue)
            this.defaultChanged.trigger(this, value);
    }

    public get isEdited(): boolean {
        return this._isEdited;
    }

    public set isEdited(value: boolean) {
        this._isEdited = value;
        if (this._sidePanelItem)
            this._sidePanelItem.isEdited = value;
        if (this._formDropDownMenuItem)
            this._formDropDownMenuItem.isEdited = value;
    }

    public get data(): IFormProfileDataContainer {
        return this._profileBasicItem.data;
    }

    public set data(value: IFormProfileDataContainer) {
        this._profileBasicItem.data = value;
    }

    public get jsonData(): string {
        return this._profileBasicItem.jsonData;
    }

    public set jsonData(value: string) {
        this._profileBasicItem.jsonData = value;
    }

    public set showInNavigationTree(value: boolean) {
        this._profileBasicItem.showInNavigationTree = value;
    }

    public get showInNavigationTree(): boolean {
        return this._profileBasicItem.showInNavigationTree;
    }

    /** Režim formuláře, ve kterém nejsou při jeho otevření načteny žádná data */
    public set queryMode(value: boolean | null) {
        this._profileBasicItem.queryMode = value;
    }

    /** Režim formuláře, ve kterém nejsou při jeho otevření načteny žádná data */
    public get queryMode(): boolean | null {
        return this._profileBasicItem.queryMode;
    }

    public get storeFilters(): boolean | null {
        return this._profileBasicItem.storeFilters;
    }
    public set storeFilters(v: boolean | null) {
        this._profileBasicItem.storeFilters = v;
    }

    public set isImplementation(value: boolean) {
        this._profileBasicItem.isImplementation = value;
        this._formDropDownMenuItem.isImplementation = value;
    }

    public get isImplementation(): boolean {
        return this._profileBasicItem.isImplementation;
    }

    /**
     * Zda je profil základním profilem, tedy systémovým profilem bez jakýchkoliv úprav uživatelem
     */
    public get basic(): boolean {
        return this._profileBasicItem.basic;
    }

    public setSidePanelItem(sidePanelItem: FormProfileItemBuilder): void {
        this._sidePanelItem = sidePanelItem;
        this._sidePanelItem.isEdited = this._isEdited;
    }

    public setFormDropDownMenuItem(formDropDownMenuItem: DropDownMenuFormProfileItem): void {
        this._formDropDownMenuItem = formDropDownMenuItem;
        if (this._formDropDownMenuItem == null)
            return;

        this._formDropDownMenuItem.isEdited = this._isEdited;
        this._formDropDownMenuItem.defaultProfile = this._profileBasicItem.defaultProfile;
        this._formDropDownMenuItem.selected = this._profileBasicItem.selected;
        this._formDropDownMenuItem.profileName = this._profileBasicItem.name;
        this._formDropDownMenuItem.isImplementation = this._profileBasicItem.isImplementation;
    }

    constructor(formCode: string, formProfileBasicItem: FormProfileBasicItem, dropDownMenuItem: DropDownMenuFormProfileItem = null) {
        this._formCode = formCode;
        this._profileBasicItem = formProfileBasicItem;
        this.setFormDropDownMenuItem(dropDownMenuItem);
    }

    public getProfileBasicItem(): FormProfileBasicItem {
        return this._profileBasicItem;
    }

    public getFormDropDownMenuItem(): DropDownMenuFormProfileItem {
        return this._formDropDownMenuItem;
    }

    public select(): void {
        this.selected = true;
    }

    /**
     * Odstraní položku profilu. Po odstranění již položka nevysílá žádné události. Neuvolňuje však své zdroje, takže je možné položku použít např. jako parametr
     * pro událost profileRemoved v {@link FormProfileManager}. K uvolnění zdrojů je nutné zavolat {@link FormProfileItem.dispose}.
     */
    public remove(): void {
        this.selectPreview.removeAll();
        this.selectionChanged.removeAll();
        this.defaultChanged.removeAll();
    }

    public discardChanges(): void {
        this.data = this.jsonData ? JSON.parse(this.jsonData) : {};
        this.isEdited = false;
    }

    public isDisposed(): boolean {
        return this._isDisposed;
    }

    public dispose(): void {
        this.selectPreview.removeAll();
        this.selectionChanged.removeAll();
        this.defaultChanged.removeAll();

        this._sidePanelItem?.remove();
        this._formDropDownMenuItem?.remove();
        this._formDropDownMenuItem?.dispose();

        this._isDisposed = true;
    }
}
